import type { ExpressionSpecification, LayerSpecification } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import mlcontour from 'maplibre-contour';
import { DEM_ENCODING, DEM_TILES_URL } from '@/features/elevation';
import { UNIT_SYSTEMS, type UnitSystem } from '@/features/units';
import type { Basemap } from './basemapAtom';

// Contours are generated on the fly from the DEM and drawn over either basemap.

export const CONTOURS_SOURCE = 'contours';
export const CONTOUR_LINES_LAYER = 'contour-lines';
export const CONTOUR_LABELS_LAYER = 'contour-labels';

/** zoom → [minor, major] interval in the unit system's height unit; major lines are labelled. */
const THRESHOLDS: Record<UnitSystem, Record<number, [number, number]>> = {
  metric: { 10: [200, 1000], 11: [100, 500], 12: [50, 250], 13: [20, 100], 15: [10, 50] },
  imperial: { 10: [500, 2500], 11: [250, 1000], 12: [200, 1000], 13: [100, 500], 15: [40, 200] },
};

let demSource: InstanceType<typeof mlcontour.DemSource> | undefined;

/** Lazily creates the contour DEM source and registers its protocol with MapLibre. */
function contourDem() {
  if (!demSource) {
    demSource = new mlcontour.DemSource({
      url: DEM_TILES_URL,
      encoding: DEM_ENCODING,
      maxzoom: 13,
      worker: true,
    });
    demSource.setupMaplibre(maplibregl);
  }
  return demSource;
}

export const contourTilesUrl = (units: UnitSystem) =>
  contourDem().contourProtocolUrl({
    multiplier: 1 / UNIT_SYSTEMS[units].height.metres,
    thresholds: THRESHOLDS[units],
  });

export const contourLabel = (units: UnitSystem): ExpressionSpecification => [
  'concat',
  ['number-format', ['get', 'ele'], {}],
  ` ${UNIT_SYSTEMS[units].height.symbol}`,
];

/** Contour colours per basemap: light over imagery, brown (like paper topo maps) over the default map. */
export const CONTOUR_PAINT: Record<Basemap, { line: string; text: string; halo: string }> = {
  satellite: { line: 'rgba(255,255,255,.55)', text: '#fff', halo: 'rgba(0,0,0,.7)' },
  default: { line: 'rgba(150,100,50,.45)', text: '#7a5230', halo: 'rgba(255,255,255,.85)' },
};

export const contourLayers = (units: UnitSystem, basemap: Basemap): LayerSpecification[] => {
  const paint = CONTOUR_PAINT[basemap];
  return [
    {
      id: CONTOUR_LINES_LAYER,
      type: 'line',
      source: CONTOURS_SOURCE,
      'source-layer': 'contours',
      paint: {
        'line-color': paint.line,
        'line-width': ['match', ['get', 'level'], 1, 1.1, 0.5],
      },
    },
    {
      id: CONTOUR_LABELS_LAYER,
      type: 'symbol',
      source: CONTOURS_SOURCE,
      'source-layer': 'contours',
      filter: ['>', ['get', 'level'], 0],
      layout: {
        'symbol-placement': 'line',
        'text-size': 10,
        'text-font': ['Open Sans Bold'],
        'text-field': contourLabel(units),
      },
      paint: { 'text-color': paint.text, 'text-halo-color': paint.halo, 'text-halo-width': 1.2 },
    },
  ];
};
